/**
 * Theme-level wishlist (no app / no backend).
 *
 * Storage is the browser's localStorage, so the wishlist is per-device/per-browser
 * and not tied to a customer account — the expected tradeoff of an app-free wishlist.
 * We store only product handles (order preserved, most-recent first). The wishlist
 * page re-fetches each product's real theme product card via the Section Rendering
 * API (`?section_id=wishlist-product-card`) so the cards match the collection page
 * exactly and stay price/stock-accurate.
 *
 * Custom elements:
 *   <wishlist-button data-handle="...">  toggle heart on cards + PDP
 *   <wishlist-count>                     header badge showing saved count
 *   <wishlist-list data-card-section>    wishlist page grid renderer
 */

import { CartAddEvent } from '@theme/events';

const STORAGE_KEY = 'theme:wishlist';
const CHANGE_EVENT = 'wishlist:change';
const TOAST_DURATION = 2200;

const Wishlist = {
  /** @returns {string[]} */
  read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  },
  /** @param {string[]} list */
  write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Ignore write failures (private mode / quota) — the UI still updates for this pageview.
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
  },
  has(handle) {
    return this.read().includes(handle);
  },
  add(handle) {
    const list = this.read();
    if (!list.includes(handle)) {
      list.unshift(handle);
      this.write(list);
    }
  },
  remove(handle) {
    this.write(this.read().filter((item) => item !== handle));
  },
  /** @returns {boolean} true if the product ended up added */
  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },
  count() {
    return this.read().length;
  },
};

/** Shared bottom-center toast ("Added to wishlist"). */
let toastEl;
function showToast(message) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'wishlist-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  // Force reflow so re-triggering the animation works when spam-clicking.
  toastEl.classList.remove('is-visible');
  void toastEl.offsetWidth;
  toastEl.classList.add('is-visible');
  clearTimeout(toastEl._timer);
  toastEl._timer = setTimeout(() => toastEl.classList.remove('is-visible'), TOAST_DURATION);
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    if (!this.handle) return;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    // Sections that render this element (e.g. the header) can be silently re-fetched
    // and morphed shortly after load (see sections/header.liquid's lazy hydration, and
    // any future cart/section morph) — that overwrites our synced state with the
    // server's localStorage-unaware default. Watch for it and re-sync immediately.
    this.#observer = new MutationObserver(this.#onMutate);
    this.#observer.observe(this, { attributes: true, attributeFilter: ['class', 'aria-pressed'] });
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.#observer?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  #onClick = (event) => {
    // Prevent activating the product card's overlay link underneath.
    event.preventDefault();
    event.stopPropagation();
    const added = Wishlist.toggle(this.handle);
    if (added) showToast(this.dataset.addedText || 'Added to wishlist');
  };

  #onKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#onClick(event);
    }
  };

  #onMutate = () => {
    const active = Wishlist.has(this.handle);
    if (this.classList.contains('is-active') !== active) this.#sync();
  };

  #sync = () => {
    const active = Wishlist.has(this.handle);
    this.classList.toggle('is-active', active);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.setAttribute(
      'aria-label',
      active ? this.dataset.removeLabel || 'Remove from wishlist' : this.dataset.addLabel || 'Add to wishlist'
    );
  };
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#sync);
    // See WishlistButton's connectedCallback: guards against section hydration/morphs
    // (e.g. the header's lazy hydration) reverting this back to its server default.
    this.#observer = new MutationObserver(this.#onMutate);
    this.#observer.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true, characterData: true, subtree: true });
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
    this.#observer?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  #onMutate = () => {
    const count = Wishlist.count();
    if (this.textContent !== String(count) || this.hasAttribute('hidden') !== (count === 0)) this.#sync();
  };

  #sync = () => {
    const count = Wishlist.count();
    this.textContent = String(count);
    this.toggleAttribute('hidden', count === 0);
  };
}

class WishlistList extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.loadingState = this.querySelector('[data-wishlist-loading]');
    this.cardSection = this.dataset.cardSection || 'wishlist-product-card';
    // Lives outside this element (next to the page heading), so query the document.
    this.addAllButton = document.querySelector('[data-wishlist-add-all]');
    this.addAllButton?.addEventListener('click', this.#onAddAllClick);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    await this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.addAllButton?.removeEventListener('click', this.#onAddAllClick);
  }

  #onChange = () => this.render();

  #productUrl(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}products/${handle}?section_id=${this.cardSection}`;
  }

  async #fetchCard(handle) {
    try {
      const response = await fetch(this.#productUrl(handle));
      if (!response.ok) return '';
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('[data-wishlist-card]');
      return card ? card.outerHTML : '';
    } catch {
      return '';
    }
  }

  async render() {
    if (!this.grid) return;
    const handles = Wishlist.read();
    if (this.addAllButton) this.addAllButton.hidden = !handles.length;

    if (!handles.length) {
      this.grid.innerHTML = '';
      if (this.loadingState) this.loadingState.hidden = true;
      if (this.emptyState) this.emptyState.hidden = false;
      return;
    }

    if (this.emptyState) this.emptyState.hidden = true;
    if (this.loadingState) this.loadingState.hidden = false;

    // Fetch all cards in parallel, then render in saved order.
    const cards = await Promise.all(handles.map((handle) => this.#fetchCard(handle)));

    this.grid.innerHTML = '';
    for (const card of cards) {
      if (card) this.grid.insertAdjacentHTML('beforeend', card);
    }

    if (this.loadingState) this.loadingState.hidden = true;
    // Every fetch failed / all products unavailable — show the empty state.
    if (!this.grid.children.length && this.emptyState) this.emptyState.hidden = false;
  }

  /** Fetches each wishlisted product's JSON to resolve an addable variant. */
  async #resolveCartItems() {
    const handles = Wishlist.read();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );

    const items = [];
    for (const product of products) {
      const variant = product?.variants?.find((v) => v.available);
      if (variant) items.push({ id: variant.id, quantity: 1 });
    }
    return items;
  }

  #onAddAllClick = async () => {
    const button = this.addAllButton;
    if (!button || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;

    try {
      const items = await this.#resolveCartItems();

      if (!items.length) {
        showToast(button.dataset.emptyText || 'These items are currently out of stock');
        return;
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Add to cart failed');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

      document.dispatchEvent(
        new CartAddEvent({}, 'wishlist-add-all', {
          source: 'wishlist-add-all',
          itemCount: cart.item_count,
        })
      );

      showToast(button.dataset.addedText || 'Added to cart');
    } catch {
      showToast(button.dataset.emptyText || 'These items are currently out of stock');
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  };
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-list')) customElements.define('wishlist-list', WishlistList);
